/**
 * ThemeModeProvider — gestiona el tema del Admin Web: `light` | `dark` | `system`.
 *
 * - Modo oscuro por defecto (Guía §7).
 * - La elección del usuario se persiste en `localStorage` y sobrevive recargas.
 * - Si la elección es `system`, se escucha `prefers-color-scheme` en vivo.
 * - Escribe `data-theme` y `color-scheme` en `<html>` para el chrome CSS del layout.
 *
 * No renderiza antd: el consumidor lee `resolvedTheme` y lo pasa a
 * `createAdminTheme()` dentro de `<ConfigProvider>`.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { applyThemeCssVariables, type ThemeName } from '../tokens';

export type ThemeMode = ThemeName | 'system';

/** Clave de persistencia; la usa también el script anti-FOUC de `index.html`. */
export const THEME_STORAGE_KEY = 'goelectric.admin.theme';

interface ThemeModeContextValue {
  /** Preferencia del usuario (puede ser `system`). */
  mode: ThemeMode;
  /** Tema efectivo aplicado (nunca `system`). */
  resolvedTheme: ThemeName;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

function readStoredMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(stored) ? stored : 'dark';
  } catch {
    return 'dark';
  }
}

function resolveTheme(mode: ThemeMode): ThemeName {
  if (mode !== 'system') {
    return mode;
  }
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'dark';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Aplica el tema efectivo al documento (atributo + variables CSS). */
function applyDocumentTheme(theme: ThemeName): void {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
  applyThemeCssVariables(theme, root);
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [resolvedTheme, setResolvedTheme] = useState<ThemeName>(() => resolveTheme(readStoredMode()));

  useEffect(() => {
    const nextTheme = resolveTheme(mode);
    setResolvedTheme(nextTheme);
    applyDocumentTheme(nextTheme);

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      // Almacenamiento no disponible (modo privado): el tema aplica solo a la sesión.
    }

    // En modo `system` seguimos los cambios del SO en vivo.
    if (mode !== 'system') {
      return;
    }
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const theme = resolveTheme('system');
      setResolvedTheme(theme);
      applyDocumentTheme(theme);
    };
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => setModeState(next), []);
  const value = useMemo<ThemeModeContextValue>(
    () => ({ mode, resolvedTheme, setMode }),
    [mode, resolvedTheme, setMode],
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

/** Hook de acceso al tema. Debe usarse dentro de `<ThemeModeProvider>`. */
export function useThemeMode(): ThemeModeContextValue {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode debe usarse dentro de <ThemeModeProvider>');
  }
  return context;
}
